import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiUser, FiLock, FiMail, FiPhone, FiAlertCircle, FiCheckCircle } from 'react-icons/fi';
import logoimg from '../../Components/Images/logonew (2).png';
import config from '../../config/config';
import axios from 'axios';

const UserLogin = () => {
    const [isLogin, setIsLogin] = useState(true);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null); // { type: 'success' | 'error', text: '' }
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        password: ''
    });

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.type === 'email' ? 'email' : e.target.type === 'tel' ? 'phone' : e.target.type === 'text' ? 'name' : 'password']: e.target.value });
        // Simplified mapping for the custom input types used below
    };

    // Explicit handlers for clarity with the icon-based input structure
    const handleNameChange = (e) => setFormData({ ...formData, name: e.target.value });
    const handleEmailChange = (e) => setFormData({ ...formData, email: e.target.value });
    const handlePhoneChange = (e) => setFormData({ ...formData, phone: e.target.value });
    const handlePasswordChange = (e) => setFormData({ ...formData, password: e.target.value });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage(null);
        setLoading(true);

        const endpoint = isLogin
            ? `${config.API_BASE_URL}/api/user/login`
            : `${config.API_BASE_URL}/api/user/register`;

        try {
            // Mocking the request structure - assuming standard fields
            const payload = isLogin
                ? { email: formData.email, password: formData.password }
                : {
                    name: formData.name,
                    email: formData.email,
                    phone: formData.phone,
                    password: formData.password
                };

            const response = await axios.post(endpoint, payload);

            if (response.data.success || response.status === 200 || response.status === 201) {
                setMessage({ type: 'success', text: isLogin ? 'Login Successful!' : 'Registration Successful! content...' });

                if (isLogin) {
                    // Store token/user data
                    localStorage.setItem('user_token', response.data.token);
                    localStorage.setItem('user_info', JSON.stringify(response.data.user));
                    setTimeout(() => navigate('/'), 1000);
                } else {
                    // Switch to login or auto-login
                    setTimeout(() => setIsLogin(true), 1500);
                }
            } else {
                setMessage({ type: 'error', text: response.data.message || 'Something went wrong.' });
            }

        } catch (error) {
            console.error("Auth Error:", error);
            const errorMsg = error.response?.data?.message || 'Server connection failed. Please try again.';
            setMessage({ type: 'error', text: errorMsg });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-900 to-black p-4">
            <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden relative">

                {/* Decorative Elements */}
                <div className="absolute -top-20 -left-20 w-40 h-40 bg-orange-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
                <div className="absolute -bottom-20 -right-20 w-40 h-40 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>

                <div className="p-8 relative z-10">
                    {/* Header */}
                    <div className="text-center mb-8">
                        <Link to="/">
                            <img src={logoimg} alt="Get Fast Education" className="h-12 w-auto mx-auto mb-4 object-contain brightness-0 invert" />
                        </Link>
                        <h2 className="text-3xl font-bold text-white mb-2">{isLogin ? 'Welcome Back' : 'Create Account'}</h2>
                        <p className="text-gray-300 text-sm">
                            {isLogin ? 'Enter your details to access your account' : 'Join us to explore the best colleges'}
                        </p>
                    </div>

                    {/* Alert Message */}
                    {message && (
                        <div className={`mb-6 p-3 rounded-lg flex items-center gap-3 text-sm font-medium ${message.type === 'success' ? 'bg-green-500/20 text-green-300 border border-green-500/30' : 'bg-red-500/20 text-red-300 border border-red-500/30'}`}>
                            {message.type === 'success' ? <FiCheckCircle size={20} /> : <FiAlertCircle size={20} />}
                            {message.text}
                        </div>
                    )}

                    {/* Form */}
                    <form className="space-y-4" onSubmit={handleSubmit}>
                        {!isLogin && (
                            <div className="relative group">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <FiUser className="text-gray-400 group-focus-within:text-orange-500 transition-colors" />
                                </div>
                                <input
                                    type="text"
                                    placeholder="Full Name"
                                    required={!isLogin}
                                    value={formData.name}
                                    onChange={handleNameChange}
                                    className="w-full bg-black/20 border border-white/10 rounded-lg py-3 pl-10 pr-4 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                                />
                            </div>
                        )}

                        <div className="relative group">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <FiMail className="text-gray-400 group-focus-within:text-orange-500 transition-colors" />
                            </div>
                            <input
                                type="email"
                                placeholder="Email Address"
                                required
                                value={formData.email}
                                onChange={handleEmailChange}
                                className="w-full bg-black/20 border border-white/10 rounded-lg py-3 pl-10 pr-4 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                            />
                        </div>

                        {!isLogin && (
                            <div className="relative group">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <FiPhone className="text-gray-400 group-focus-within:text-orange-500 transition-colors" />
                                </div>
                                <input
                                    type="tel"
                                    placeholder="Phone Number"
                                    required={!isLogin}
                                    value={formData.phone}
                                    onChange={handlePhoneChange}
                                    className="w-full bg-black/20 border border-white/10 rounded-lg py-3 pl-10 pr-4 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                                />
                            </div>
                        )}

                        <div className="relative group">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <FiLock className="text-gray-400 group-focus-within:text-orange-500 transition-colors" />
                            </div>
                            <input
                                type="password"
                                placeholder="Password"
                                required
                                value={formData.password}
                                onChange={handlePasswordChange}
                                className="w-full bg-black/20 border border-white/10 rounded-lg py-3 pl-10 pr-4 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                            />
                        </div>

                        {isLogin && (
                            <div className="flex justify-end">
                                <a href="#" className="text-xs text-orange-400 hover:text-orange-300 transition-colors">Forgot Password?</a>
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            className={`w-full bg-gradient-to-r from-orange-500 to-pink-500 text-white font-bold py-3 rounded-lg shadow-lg hover:shadow-orange-500/30 transform hover:scale-[1.02] transition-all duration-300 flex justify-center items-center ${loading ? 'opacity-70 cursor-wait' : ''}`}
                        >
                            {loading ? (
                                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            ) : (
                                isLogin ? 'Sign In' : 'Sign Up'
                            )}
                        </button>
                    </form>

                    {/* Toggle */}
                    <div className="mt-6 text-center text-gray-300 text-sm">
                        {isLogin ? "Don't have an account? " : "Already have an account? "}
                        <button
                            onClick={() => { setIsLogin(!isLogin); setMessage(null); }}
                            className="text-orange-400 font-bold hover:text-orange-300 transition-colors ml-1"
                        >
                            {isLogin ? 'Sign Up' : 'Login'}
                        </button>
                    </div>

                    {/* Social Login (Optional) */}
                    <div className="mt-6 pt-6 border-t border-white/10 text-center">
                        <p className="text-xs text-gray-500 mb-3 uppercase tracking-wide">Or continue with</p>
                        <div className="flex justify-center gap-4">
                            <button className="bg-white/5 hover:bg-white/10 p-2 rounded-full transition-colors border border-white/10">
                                <img src="https://www.svgrepo.com/show/475656/google-color.svg" alt="Google" className="w-6 h-6" />
                            </button>
                            <button className="bg-white/5 hover:bg-white/10 p-2 rounded-full transition-colors border border-white/10">
                                <img src="https://www.svgrepo.com/show/448234/linkedin.svg" alt="LinkedIn" className="w-6 h-6" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserLogin;
